export const SITE_NAME = "Bookling";
export const SITE_URL = "https://gdiy-books.thomaub.dev";
export const SITE_DESCRIPTION =
  "A searchable index of books mentioned by guests on Generation Do It Yourself podcast episodes.";
export const DEFAULT_SOCIAL_IMAGE = "/logo512.png";

type SeoOptions = {
  title: string;
  description?: string;
  path?: string;
  image?: string | null;
  type?: "website" | "article" | "book";
};

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

export function absoluteUrl(path = "/") {
  return new URL(path, SITE_URL).toString();
}

export function bookPath(slug: string) {
  return `/books/${encodeURIComponent(slug)}`;
}

export function seoMeta({
  title,
  description = SITE_DESCRIPTION,
  path = "/",
  image = DEFAULT_SOCIAL_IMAGE,
  type = "website",
}: SeoOptions): Array<MetaTag> {
  const url = absoluteUrl(path);
  const imageUrl = image ? absoluteUrl(image) : null;
  const meta: Array<MetaTag> = [
    { title },
    { name: "description", content: description },
    { property: "og:type", content: type },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { name: "twitter:card", content: imageUrl ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];

  if (imageUrl) {
    meta.push(
      { property: "og:image", content: imageUrl },
      { name: "twitter:image", content: imageUrl },
    );
  }

  return meta;
}

export function canonicalLink(path: string) {
  return [{ rel: "canonical", href: absoluteUrl(path) }];
}

export function jsonLdScript(data: unknown) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(data),
  };
}

type BookCollectionItem = {
  slug: string;
  title: string;
  author: string;
  imageUrl?: string | null;
};

export function bookCollectionJsonLd({
  name,
  description,
  path,
  books,
}: {
  name: string;
  description: string;
  path: string;
  books: ReadonlyArray<BookCollectionItem>;
}) {
  return jsonLdScript({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: absoluteUrl(path),
    mainEntity: {
      "@type": "ItemList",
      name: "Books mentioned on Generation Do It Yourself",
      numberOfItems: books.length,
      itemListElement: books.slice(0, 50).map((book, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: absoluteUrl(bookPath(book.slug)),
        item: {
          "@type": "Book",
          name: book.title,
          author: { "@type": "Person", name: book.author },
          image: book.imageUrl ?? undefined,
          url: absoluteUrl(bookPath(book.slug)),
        },
      })),
    },
  });
}
